import { useState, useEffect } from 'react';
import { AppMeta } from '@xnav/types/src/common.ts';
import {
  Box, Button, Flex, Heading, IconButton, Image, Text, Divider,
  useToast, useColorModeValue, Badge, Stack, HStack, Menu, MenuButton, MenuList, MenuItem, MenuDivider,
  Popover, PopoverTrigger, PopoverContent, PopoverBody, PopoverArrow, PopoverCloseButton, Portal, Tooltip,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Textarea, FormControl,
  Table, Thead, Tbody, Tr, Th, Td, Spinner, ModalCloseButton, SimpleGrid
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faEye, faTrash, faChevronDown, faUpload, faCheck, faTimes, faLock, faGlobe, faRocket, faListAlt, faBell, faEllipsisV, faRotateLeft, faBoxArchive, faFolder, faVideo, faImage, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import AppForm from './appForm';
import { apiGetRequest, logCourseAction } from '@/functions/requests';
import { useS3Client } from '@/hooks/contextHooks';
import { GetObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';

function formatBytes(bytes: number, decimals = 2) {
  if (!+bytes) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

function formatDateShort(dateString: string | Date | undefined) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const m = date.getMonth() + 1;
  const d = date.getDate();
  const y = date.getFullYear().toString().slice(-2);
  return `${m}/${d}/${y}`;
}

const getActionBadge = (action: string) => {
  const act = (action || '').toLowerCase();
  if (act.includes('deploy') && !act.includes('disapprov')) {
    return { bg: "rgba(16, 185, 129, 0.14)", color: "#10b981" };
  }
  if (act.includes('disapprov') || act.includes('delete') || act.includes('cancel')) {
    return { bg: "rgba(239, 68, 68, 0.14)", color: "#f87171" };
  }
  if (act.includes('structure') || act.includes('section') || act.includes('quiz')) {
    return { bg: "rgba(14, 165, 233, 0.14)", color: "#38bdf8" };
  }
  return { bg: "rgba(99, 102, 241, 0.14)", color: "#818cf8" };
};

const DetailsCell: React.FC<{ details: string }> = ({ details }) => {
  const textColor = useColorModeValue('gray.700', 'gray.200');

  if (!details) return <Text color="gray.500" fontSize="xs" fontStyle="italic">-</Text>;

  return (
    <Box minW="260px" maxW="580px" py={0.5}>
      <Text 
        fontSize="xs" 
        color={textColor}
        lineHeight="1.5"
        wordBreak="break-word"
        whiteSpace="pre-wrap"
        fontWeight="medium"
      >
        {details}
      </Text>
    </Box>
  );
};

const AppCard: React.FC<{
  data: AppMeta;
  onDelete: (app: AppMeta) => void;
  onArchive?: (app: AppMeta) => void;
  onUnarchive?: (app: AppMeta) => void;
  onEdit: (newApp: AppMeta, icon?: File) => void;
  onValidate: (newApp: AppMeta) => string | undefined;
  viewMode?: 'list' | 'grid' | 'landscape';
  userRole?: string | null;
  userOrg?: string | null;
  currentUserId?: string;
  currentUserEmail?: string;
}> = ({ data, onEdit, onDelete, onArchive, onUnarchive, onValidate, viewMode = 'list', userRole, currentUserId, currentUserEmail }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [editingApp, setEditingApp] = useState(false);
  const [confirmDeletion, setConfirmDeletion] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [selectedCourseType, setSelectedCourseType] = useState(data.courseType || 'Beginner');
  const [disapproveType, setDisapproveType] = useState<'deploy' | 'delete' | null>(null);
  const [disapproveReason, setDisapproveReason] = useState('');
  const [viewDisapprovalType, setViewDisapprovalType] = useState<'deploy' | 'delete' | 'cancelDeploy' | 'cancelDelete' | null>(null);
  
  const [viewingLogs, setViewingLogs] = useState(false);
  const [logs, setLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const s3 = useS3Client();
  const [courseStats, setCourseStats] = useState<{
    videos: number;
    images: number;
    pdfs: number;
    loaded: boolean;
    loading: boolean;
  }>({
    videos: data.stats?.videos || 0,
    images: data.stats?.images || 0,
    pdfs: data.stats?.pdfs || 0,
    loaded: !!data.stats,
    loading: false
  });

  useEffect(() => {
    if (data.stats) {
      setCourseStats({
        videos: data.stats.videos || 0,
        images: data.stats.images || 0,
        pdfs: data.stats.pdfs || 0,
        loaded: true,
        loading: false
      });
    }
  }, [data.stats]);

  const fetchCourseStats = async () => {
    if (courseStats.loaded || courseStats.loading) return;
    setCourseStats(prev => ({ ...prev, loading: true }));
    try {
      let videos = 0;
      let images = 0;
      let pdfs = 0;

      // Fetch media metadata (to count videos, photos/images, pdfs)
      try {
        let mediaData: any[] | null = null;
        if (s3) {
          try {
            const metaCommand = new GetObjectCommand({
              Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
              Key: `${data.id}/media/meta.json`
            });
            const metaUrl = await getSignedUrl(s3 as any, metaCommand as any, { expiresIn: 300 });
            const resp = await fetch(metaUrl);
            if (resp.ok) {
              mediaData = await resp.json();
            }
          } catch (e) {
            // fallback
          }
        }
        if (!mediaData) {
          mediaData = await apiGetRequest<string, any[]>(`/media/${data.id}`);
        }

        if (Array.isArray(mediaData)) {
          for (const m of mediaData) {
            // MediaType: 0 = Video, 1 = Audio, 2 = Pdf, 3 = Image
            if (m.type === 0 || m.type === 'video') videos += 1;
            else if (m.type === 3 || m.type === 'image') images += 1;
            else if (m.type === 2 || m.type === 'pdf') pdfs += 1;
          }
        }
      } catch (e) {
        console.error("Error loading media stats", e);
      }

      setCourseStats({
        videos,
        images,
        pdfs,
        loaded: true,
        loading: false
      });
    } catch (e) {
      setCourseStats(prev => ({ ...prev, loaded: true, loading: false }));
    }
  };

  const CourseNotificationBell = () => {
    const activeCount = (data.deleteRequested ? 1 : 0) + (data.deployRequested ? 1 : 0);
    if (activeCount === 0) return null;

    const isAdminUser = isSuperAdmin || isAdmin || isOrgAdmin;

    return (
      <Box position="relative" display="inline-block" ml={1}>
        <Popover placement="bottom-end" closeOnBlur={true}>
          <PopoverTrigger>
            <IconButton
              aria-label="Course Actions Bell"
              icon={<FontAwesomeIcon icon={faBell} size="sm" />}
              variant="ghost"
              color={useColorModeValue("orange.500", "orange.400")}
              borderRadius="full"
              size="xs"
              _hover={{ bg: useColorModeValue("orange.50", "whiteAlpha.150") }}
            />
          </PopoverTrigger>
          <Flex
            position="absolute"
            top="-3px"
            right="-3px"
            bg="red.500"
            color="white"
            borderRadius="full"
            w="16px"
            h="16px"
            align="center"
            justify="center"
            fontSize="9px"
            fontWeight="bold"
            shadow="md"
            pointerEvents="none"
          >
            {activeCount}
          </Flex>
          <Portal>
            <PopoverContent
              bg={useColorModeValue("white", "gray.850")}
              borderColor={useColorModeValue("green.100", "green.900/30")}
              borderRadius="2xl"
              shadow="2xl"
              w="320px"
              p={0}
              overflow="hidden"
              zIndex={9999}
              _focus={{ outline: 'none' }}
            >
              <PopoverArrow bg={useColorModeValue("white", "gray.850")} />
              <PopoverCloseButton borderRadius="full" size="sm" top={3.5} right={3.5} zIndex={11} />
              
              {/* Header matching user mockup screenshot */}
              <Box p={4} pb={3}>
                <Heading size="xs" color="green.500" textTransform="uppercase" letterSpacing="widest" fontWeight="extrabold">
                  PENDING REQUESTS
                </Heading>
                <Text fontSize="10px" color="gray.500" mt={0.5}>
                  Review actions requested for this course
                </Text>
              </Box>
              
              <Divider borderColor={useColorModeValue("gray.100", "gray.750")} />
              
              <Box p={4}>
                <Stack spacing={3.5}>
                  {data.deleteRequested && (
                    <Box 
                      border="1px solid" 
                      borderColor={useColorModeValue("gray.200", "gray.700")} 
                      borderRadius="xl" 
                      p={4}
                      bg={useColorModeValue("white", "gray.800")}
                    >
                      <Text fontSize="xs" fontWeight="bold" color={titleColor} mb={3}>
                        Deletion requested by <Text as="span" color="green.500" fontWeight="bold">{data.creator || 're'}</Text>
                      </Text>
                      
                      {isAdminUser ? (
                        <Flex gap={2} w="100%">
                          <Button
                            variant="outline"
                            colorScheme="gray"
                            color="gray.600"
                            borderColor="gray.200"
                            leftIcon={<FontAwesomeIcon icon={faTimes} />}
                            onClick={() => setDisapproveType('delete')}
                            borderRadius="full"
                            size="sm"
                            fontSize="xs"
                            fontWeight="bold"
                            flex={1}
                            _hover={{ bg: useColorModeValue("gray.50", "gray.700") }}
                          >
                            Reject
                          </Button>
                          <Button
                            variant="solid"
                            colorScheme="red"
                            leftIcon={<FontAwesomeIcon icon={faCheck} />}
                            onClick={() => onDelete(data)}
                            borderRadius="full"
                            size="sm"
                            fontSize="xs"
                            fontWeight="bold"
                            flex={1}
                          >
                            Approve
                          </Button>
                        </Flex>
                      ) : (
                        <Flex justify="space-between" align="center">
                          <Badge colorScheme="red" variant="subtle" borderRadius="full" px={2.5} py={0.5}>Pending Review</Badge>
                          {isInstructor && (
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => onEdit({ ...data, deleteRequested: false })}
                              borderRadius="full"
                            >
                              Cancel Request
                            </Button>
                          )}
                        </Flex>
                      )}
                    </Box>
                  )}

                  {data.deployRequested && (
                    <Box 
                      border="1px solid" 
                      borderColor={useColorModeValue("gray.200", "gray.700")} 
                      borderRadius="xl" 
                      p={4}
                      bg={useColorModeValue("white", "gray.800")}
                    >
                      <Text fontSize="xs" fontWeight="bold" color={titleColor} mb={3}>
                        Deployment requested by <Text as="span" color="green.500" fontWeight="bold">{data.creator || 're'}</Text>
                      </Text>
                      
                      {isAdminUser ? (
                        <Flex gap={2} w="100%">
                          <Button
                            variant="outline"
                            colorScheme="gray"
                            color="gray.600"
                            borderColor="gray.200"
                            leftIcon={<FontAwesomeIcon icon={faTimes} />}
                            onClick={() => setDisapproveType('deploy')}
                            borderRadius="full"
                            size="sm"
                            fontSize="xs"
                            fontWeight="bold"
                            flex={1}
                            _hover={{ bg: useColorModeValue("gray.50", "gray.700") }}
                          >
                            Reject
                          </Button>
                          <Button
                            variant="solid"
                            colorScheme="purple"
                            leftIcon={<FontAwesomeIcon icon={faRocket} />}
                            onClick={() => navigate(`/${data.id}/deploy`)}
                            borderRadius="full"
                            size="sm"
                            fontSize="xs"
                            fontWeight="bold"
                            flex={1}
                          >
                            Go to Deploy
                          </Button>
                        </Flex>
                      ) : (
                        <Flex justify="space-between" align="center">
                          <Badge colorScheme="green" variant="subtle" borderRadius="full" px={2.5} py={0.5}>Pending Review</Badge>
                          {isInstructor && (
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="green"
                              onClick={() => onEdit({ ...data, deployRequested: false })}
                              borderRadius="full"
                            >
                              Cancel Request
                            </Button>
                          )}
                        </Flex>
                      )}
                    </Box>
                  )}
                </Stack>
              </Box>
            </PopoverContent>
          </Portal>
        </Popover>
      </Box>
    );
  };

  const fetchAppLogs = async () => {
    setViewingLogs(true);
    setLogsLoading(true);
    try {
      const res = await apiGetRequest<undefined, any[]>(`/logs/${data.id}`);
      setLogs(res.sort((a, b) => b.timestamp - a.timestamp));
    } catch (e) {
      console.error("Failed to fetch app logs", e);
      toast({ title: 'Error', description: 'Failed to fetch audit logs.', status: 'error', duration: 3000 });
    } finally {
      setLogsLoading(false);
    }
  };

  const titleColor = useColorModeValue('gray.800', 'white');
  const glassBg = useColorModeValue('rgba(255, 255, 255, 0.7)', 'rgba(26, 32, 44, 0.7)');
  const glassBorderColor = useColorModeValue('rgba(226, 232, 240, 0.8)', 'rgba(45, 55, 72, 0.8)');
  const glassBorder = `1px solid ${glassBorderColor}`;
  const glassShadow = useColorModeValue('0 8px 32px 0 rgba(31, 38, 135, 0.04)', '0 8px 32px 0 rgba(0, 0, 0, 0.3)');

  const handleCourseTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newCourseType = event.target.value;
    setSelectedCourseType(newCourseType);

    const updatedApp = { ...data, courseType: newCourseType };
    onEdit(updatedApp);

    toast({
      title: 'Course Type Updated',
      description: `Course type changed to "${newCourseType}" successfully.`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleArchiveClick = () => {
    if (data.deployed) {
      setConfirmArchive(true);
    } else {
      if (onArchive) onArchive(data);
      else onEdit({ ...data, archived: true });
    }
  };

  if (confirmDeletion) {
    return (
      <Box
        p={4}
        bg="red.50"
        borderRadius="xl"
        border="1px dashed"
        borderColor="red.300"
        display="flex"
        alignItems="center"
        justifyContent="center"
        h="100%"
      >
        <Flex direction="column" gap={4} align="center" textAlign="center">
          <Text fontSize="sm" fontWeight="bold" color="red.700">Permanently Delete <b>{data.name}</b>?</Text>
          <Text fontSize="xs" color="red.600">This action cannot be undone. Database metadata and deployment history will be deleted.</Text>
          <Flex gap={2}>
            <Button size="sm" colorScheme="red" onClick={() => { onDelete(data); setConfirmDeletion(false); }}>
              Permanent Delete
            </Button>
            <Button size="sm" variant="ghost" colorScheme="red" onClick={() => setConfirmDeletion(false)}>
              Cancel
            </Button>
          </Flex>
        </Flex>
      </Box>
    );
  }

  if (editingApp) {
    return (
      <Box
        p={6}
        bg={useColorModeValue("white", "#131b26")}
        borderRadius="xl"
        border="1px solid"
        borderColor={useColorModeValue("gray.200", "rgba(255, 255, 255, 0.08)")}
        shadow="2xl"
        my={2}
      >
        <Heading size="sm" fontSize="md" mb={5} color={useColorModeValue("gray.800", "white")} display="flex" alignItems="center" gap={2.5}>
          <FontAwesomeIcon icon={faEdit} color="#0ea5e9" />
          Edit Course Details — <Text as="span" color="#0ea5e9" fontWeight="bold">{data.name}</Text>
        </Heading>
        <AppForm
          app={{ ...data, courseType: selectedCourseType }}
          onAdd={(newApp, icon) => {
            onEdit(newApp, icon);
            setEditingApp(false);
          }}
          onCancel={() => setEditingApp(false)}
          onValidate={onValidate}
          userRole={userRole}
        />
      </Box>
    );
  }

  const isDeployed = data.deployed !== undefined;
  const isUpToDate = isDeployed && data.deployed === data.edited;
  const formattedSize = formatBytes(data.size || 0);

  const needsDeploy = !isDeployed || !isUpToDate;

  const isSuperAdmin = userRole === 'Super Admin';
  const isAdmin = userRole === 'Admin';
  const isOrgAdmin = userRole === 'Org Admin';
  const isInstructor = userRole === 'Instructor';

  const isCreatorOrAdmin = isSuperAdmin || isAdmin || isOrgAdmin || data.creatorId === currentUserId;

  const isCreatorOrCollab = isCreatorOrAdmin || 
    (currentUserId && data.collaborators?.includes(currentUserId)) || 
    (currentUserEmail && data.collaborators?.some(c => c.toLowerCase() === currentUserEmail.toLowerCase()));
  
  const isOnlyReviewer = !isCreatorOrCollab && (
    (currentUserId && data.reviewers?.includes(currentUserId)) || 
    (currentUserEmail && data.reviewers?.some(r => r.toLowerCase() === currentUserEmail.toLowerCase()))
  );

  const handleRequestDeploy = () => {
    onEdit({ ...data, deployRequested: true, deployDisapprovedReason: undefined });
    toast({ title: 'Deploy Requested', description: 'Admins have been notified.', status: 'info', duration: 3000, isClosable: true });
    logCourseAction(data.id, "Deploy Requested", `User ${currentUserEmail} requested deployment.`);
  }

  const handleRequestDelete = () => {
    onEdit({ ...data, deleteRequested: true, deleteDisapprovedReason: undefined });
    toast({ title: 'Delete Requested', description: 'Super Admins have been notified.', status: 'info', duration: 3000, isClosable: true });
    logCourseAction(data.id, "Delete Requested", `User ${currentUserEmail} requested deletion.`);
  }


  const handleDisapprove = () => {
    if (disapproveType === 'deploy') {
      onEdit({ ...data, deployRequested: false, deployDisapprovedReason: disapproveReason });
      toast({ title: 'Deploy Disapproved', description: 'The reason has been saved and is now visible to the instructor.', status: 'warning', duration: 4000, isClosable: true });
      logCourseAction(data.id, "Deploy Disapproved", `Admin ${currentUserEmail} disapproved deployment. Reason: ${disapproveReason}`);
    } else if (disapproveType === 'delete') {
      onEdit({ ...data, deleteRequested: false, deleteDisapprovedReason: disapproveReason });
      toast({ title: 'Delete Disapproved', description: 'The reason has been saved and is now visible to the instructor.', status: 'warning', duration: 4000, isClosable: true });
      logCourseAction(data.id, "Delete Disapproved", `Admin ${currentUserEmail} disapproved deletion. Reason: ${disapproveReason}`);
    }

    setDisapproveType(null);
    setDisapproveReason('');
  }

  const handleAcknowledgeDisapproval = (type: 'deploy' | 'delete') => {
    if (type === 'deploy') {
      onEdit({ ...data, deployDisapprovedReason: undefined });
      logCourseAction(data.id, "Deploy Disapproval Acknowledged", `User ${currentUserEmail} acknowledged deploy disapproval.`);
    } else {
      onEdit({ ...data, deleteDisapprovedReason: undefined });
      logCourseAction(data.id, "Delete Disapproval Acknowledged", `User ${currentUserEmail} acknowledged delete disapproval.`);
    }
  }

  // Common Actions Component
  const ActionButtons = ({ size = 'sm' }: { size?: string }) => (
    <HStack spacing={1} justify="flex-end">
      {/* Primary Action: Open Course Studio */}
      <Tooltip label="Open Course Studio" placement="top" hasArrow>
        <IconButton
          variant="ghost"
          size={size}
          borderRadius="lg"
          color={useColorModeValue("#0284c7", "#38bdf8")}
          _hover={{ bg: useColorModeValue("rgba(14, 165, 233, 0.1)", "rgba(14, 165, 233, 0.15)"), color: useColorModeValue("#0369a1", "#7dd3fc") }}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/${data.id}`);
          }}
          aria-label="Open Course Studio"
          icon={<FontAwesomeIcon icon={faEye} />}
        />
      </Tooltip>

      {/* Quick Edit Details Button */}
      {!isOnlyReviewer && isCreatorOrAdmin && (
        <Tooltip label="Edit Details" placement="top" hasArrow>
          <IconButton
            variant="ghost"
            size={size}
            borderRadius="lg"
            color={useColorModeValue("gray.600", "gray.400")}
            _hover={{ bg: useColorModeValue("gray.100", "whiteAlpha.150"), color: useColorModeValue("gray.900", "white") }}
            onClick={(e) => {
              e.stopPropagation();
              setEditingApp(true);
            }}
            aria-label="Edit Details"
            icon={<FontAwesomeIcon icon={faEdit} />}
          />
        </Tooltip>
      )}

      {/* More Actions Menu */}
      <Menu isLazy strategy="fixed" placement="bottom-end">
        <Tooltip label="More Actions" placement="top" hasArrow>
          <MenuButton
            as={IconButton}
            variant="ghost"
            size={size}
            borderRadius="lg"
            color={useColorModeValue("gray.500", "gray.400")}
            _hover={{ bg: useColorModeValue("gray.100", "whiteAlpha.150"), color: useColorModeValue("gray.900", "white") }}
            aria-label="More Actions"
            icon={<FontAwesomeIcon icon={faEllipsisV} />}
            onClick={(e) => e.stopPropagation()}
          />
        </Tooltip>
        <Portal>
          <MenuList
            zIndex={2000}
            borderRadius="xl"
            shadow="2xl"
            border="1px solid"
            borderColor={useColorModeValue("gray.200", "rgba(255, 255, 255, 0.08)")}
            p={1.5}
            bg={useColorModeValue("white", "#141c28")}
            minW="180px"
            outline="none"
            _focus={{ outline: "none", boxShadow: "2xl" }}
            _focusVisible={{ outline: "none", boxShadow: "2xl" }}
          >
            {!isOnlyReviewer && (
              <MenuItem
                borderRadius="md"
                fontSize="xs"
                fontWeight="medium"
                bg="transparent"
                color={useColorModeValue("gray.700", "gray.200")}
                _hover={{ bg: useColorModeValue("gray.100", "rgba(255, 255, 255, 0.06)"), color: useColorModeValue("gray.900", "white") }}
                _focus={{ bg: useColorModeValue("gray.100", "rgba(255, 255, 255, 0.06)") }}
                icon={<FontAwesomeIcon icon={faListAlt} color="#0ea5e9" />}
                onClick={(e) => {
                  e.stopPropagation();
                  fetchAppLogs();
                }}
              >
                View Audit Logs
              </MenuItem>
            )}

            {data.archived ? (
              <>
                {isSuperAdmin && !isOnlyReviewer && (
                  <MenuItem
                    borderRadius="md"
                    fontSize="xs"
                    fontWeight="medium"
                    icon={<FontAwesomeIcon icon={faRotateLeft} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onUnarchive ? onUnarchive(data) : onEdit({ ...data, archived: false });
                    }}
                    color="green.500"
                  >
                    Restore Course
                  </MenuItem>
                )}
                {isSuperAdmin && !isOnlyReviewer && (
                  <MenuItem
                    borderRadius="md"
                    fontSize="xs"
                    fontWeight="medium"
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmDeletion(true);
                    }}
                    color="red.500"
                  >
                    Permanently Delete
                  </MenuItem>
                )}
              </>
            ) : (
              <>
                {isInstructor && !isOnlyReviewer && needsDeploy && !data.deployRequested && (
                  <MenuItem
                    borderRadius="md"
                    fontSize="xs"
                    fontWeight="medium"
                    bg="transparent"
                    color={useColorModeValue("gray.700", "gray.200")}
                    _hover={{ bg: useColorModeValue("gray.100", "rgba(255, 255, 255, 0.06)"), color: useColorModeValue("gray.900", "white") }}
                    _focus={{ bg: useColorModeValue("gray.100", "rgba(255, 255, 255, 0.06)") }}
                    icon={<FontAwesomeIcon icon={faUpload} color="#f59e0b" />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRequestDeploy();
                    }}
                  >
                    Request Deploy
                  </MenuItem>
                )}
                {(isSuperAdmin || isAdmin || isOrgAdmin) && !isOnlyReviewer && (
                  <MenuItem
                    borderRadius="md"
                    fontSize="xs"
                    fontWeight="medium"
                    bg="transparent"
                    color={useColorModeValue("gray.700", "gray.200")}
                    _hover={{ bg: useColorModeValue("gray.100", "rgba(255, 255, 255, 0.06)"), color: useColorModeValue("gray.900", "white") }}
                    _focus={{ bg: useColorModeValue("gray.100", "rgba(255, 255, 255, 0.06)") }}
                    icon={<FontAwesomeIcon icon={faRocket} color="#0ea5e9" />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/${data.id}/deploy`);
                    }}
                  >
                    Deploy Course
                  </MenuItem>
                )}
                {isSuperAdmin && !isOnlyReviewer && (
                  <MenuItem
                    borderRadius="md"
                    fontSize="xs"
                    fontWeight="medium"
                    bg="transparent"
                    color={useColorModeValue("gray.700", "gray.200")}
                    _hover={{ bg: useColorModeValue("gray.100", "rgba(255, 255, 255, 0.06)"), color: useColorModeValue("gray.900", "white") }}
                    _focus={{ bg: useColorModeValue("gray.100", "rgba(255, 255, 255, 0.06)") }}
                    icon={<FontAwesomeIcon icon={faBoxArchive} color="#f59e0b" />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleArchiveClick();
                    }}
                  >
                    Archive Course
                  </MenuItem>
                )}
                {(isInstructor || isOrgAdmin) && !isOnlyReviewer && (
                  <>
                    <MenuDivider my={1} borderColor={useColorModeValue("gray.100", "rgba(255, 255, 255, 0.06)")} />
                    <MenuItem
                      borderRadius="md"
                      fontSize="xs"
                      fontWeight="medium"
                      bg="transparent"
                      icon={<FontAwesomeIcon icon={faTrash} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRequestDelete();
                      }}
                      color="red.400"
                      _hover={{ bg: useColorModeValue("red.50", "rgba(239, 68, 68, 0.15)"), color: "red.300" }}
                      _focus={{ bg: useColorModeValue("red.50", "rgba(239, 68, 68, 0.15)") }}
                    >
                      Request Delete
                    </MenuItem>
                  </>
                )}
              </>
            )}
          </MenuList>
        </Portal>
      </Menu>
    </HStack>
  );

  // Helper for Premium Hover Info
  const InfoPopover: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Popover trigger="hover" placement="auto" openDelay={450} closeDelay={150} onOpen={fetchCourseStats}>
      <PopoverTrigger>
        <Box cursor="pointer">{children}</Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          bg={useColorModeValue('white', 'gray.850')}
          borderColor={useColorModeValue('gray.200', 'gray.700')}
          shadow="2xl"
          borderRadius="2xl"
          w="340px"
          maxW="90vw"
          _focus={{ outline: 'none' }}
        >
          <PopoverArrow bg={useColorModeValue('white', 'gray.850')} />
          <PopoverBody p={4}>
            <Flex align="center" justify="space-between" mb={2} pb={2} borderBottom="1px solid" borderColor={useColorModeValue('gray.100', 'gray.750')}>
              <Flex align="center" gap={2}>
                <Box w="6px" h="6px" borderRadius="full" bg="teal.400" />
                <Text fontSize="11px" fontWeight="700" color={useColorModeValue('gray.500', 'gray.400')} letterSpacing="0.8px" textTransform="uppercase">
                  Course Overview
                </Text>
              </Flex>
              <Text fontSize="xs" color="gray.400" fontFamily="mono" fontWeight="medium">
                {formattedSize}
              </Text>
            </Flex>

            <Text fontSize="xs" mb={3.5} color={useColorModeValue('gray.600', 'gray.300')} lineHeight="relaxed" noOfLines={4}>
              {data.description ? data.description : (
                <Text as="span" color="gray.400" fontStyle="italic">No description provided for this course.</Text>
              )}
            </Text>

            {/* Course Content Breakdown */}
            <Box bg={useColorModeValue('gray.50', 'whiteAlpha.50')} p={2.5} borderRadius="xl" border="1px solid" borderColor={useColorModeValue('gray.100', 'whiteAlpha.100')}>
              <Text fontSize="10px" fontWeight="700" color={useColorModeValue('gray.400', 'gray.500')} textTransform="uppercase" letterSpacing="wider" mb={2}>
                Course Contents
              </Text>
              {courseStats.loading && !courseStats.loaded ? (
                <Flex align="center" justify="center" py={2}>
                  <Spinner size="xs" color="teal.500" mr={2} />
                  <Text fontSize="xs" color="gray.400">Loading contents...</Text>
                </Flex>
              ) : (
                <Flex gap={2} align="center" justify="space-between">
                  <Flex align="center" gap={2} px={2.5} py={1.5} bg={useColorModeValue('white', 'gray.800')} borderRadius="lg" border="1px solid" borderColor={useColorModeValue('gray.200', 'gray.700')} flex={1}>
                    <Flex w="24px" h="24px" align="center" justify="center" borderRadius="md" bg={useColorModeValue('purple.50', 'purple.900/40')} color="purple.500" flexShrink={0}>
                      <FontAwesomeIcon icon={faVideo} size="xs" />
                    </Flex>
                    <Box minW={0}>
                      <Text fontSize="xs" fontWeight="bold" color={titleColor} lineHeight="1">
                        {courseStats.videos}
                      </Text>
                      <Text color="gray.400" fontWeight="medium" fontSize="10px" lineHeight="1" mt={0.5}>
                        Videos
                      </Text>
                    </Box>
                  </Flex>

                  <Flex align="center" gap={2} px={2.5} py={1.5} bg={useColorModeValue('white', 'gray.800')} borderRadius="lg" border="1px solid" borderColor={useColorModeValue('gray.200', 'gray.700')} flex={1}>
                    <Flex w="24px" h="24px" align="center" justify="center" borderRadius="md" bg={useColorModeValue('teal.50', 'teal.900/40')} color="teal.500" flexShrink={0}>
                      <FontAwesomeIcon icon={faImage} size="xs" />
                    </Flex>
                    <Box minW={0}>
                      <Text fontSize="xs" fontWeight="bold" color={titleColor} lineHeight="1">
                        {courseStats.images}
                      </Text>
                      <Text color="gray.400" fontWeight="medium" fontSize="10px" lineHeight="1" mt={0.5}>
                        Photos
                      </Text>
                    </Box>
                  </Flex>

                  <Flex align="center" gap={2} px={2.5} py={1.5} bg={useColorModeValue('white', 'gray.800')} borderRadius="lg" border="1px solid" borderColor={useColorModeValue('gray.200', 'gray.700')} flex={1}>
                    <Flex w="24px" h="24px" align="center" justify="center" borderRadius="md" bg={useColorModeValue('red.50', 'red.900/40')} color="red.500" flexShrink={0}>
                      <FontAwesomeIcon icon={faFilePdf} size="xs" />
                    </Flex>
                    <Box minW={0}>
                      <Text fontSize="xs" fontWeight="bold" color={titleColor} lineHeight="1">
                        {courseStats.pdfs}
                      </Text>
                      <Text color="gray.400" fontWeight="medium" fontSize="10px" lineHeight="1" mt={0.5}>
                        PDFs
                      </Text>
                    </Box>
                  </Flex>
                </Flex>
              )}
            </Box>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );


  if (viewMode === 'landscape') {
    return (
      <Flex
        direction="column"
        bg={glassBg}
        borderRadius="2xl"
        shadow={glassShadow}
        border={glassBorder}
        backdropFilter="blur(20px)"
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        _hover={{ transform: 'translateY(-4px)', shadow: 'xl', borderColor: useColorModeValue('teal.300', 'teal.600') }}
        h="100%"
        overflow="hidden"
      >
        <Box position="relative" w="100%" paddingTop="56.25%">
          <InfoPopover>
            <Image
              position="absolute"
              top={0}
              left={0}
              w="100%"
              h="100%"
              objectFit="cover"
              src={data.iconUrl ?? 'icon.png'}
              fallbackSrc="https://via.placeholder.com/640x360"
            />
          </InfoPopover>
          <Box position="absolute" top={3} right={3} display="flex" alignItems="center" gap={1.5}>
            {data.deployDisapprovedReason ? (
              <Badge onClick={() => setViewDisapprovalType('deploy')} px={3} py={1} borderRadius="full" colorScheme="red" variant="solid" cursor="pointer">
                Deploy Disapproved
              </Badge>
            ) : data.deleteDisapprovedReason ? (
              null
            ) : !isDeployed ? (
              <Badge colorScheme="gray" px={3} py={1} borderRadius="full" shadow="md" variant="solid">Draft</Badge>
            ) : (
              <Badge colorScheme={isUpToDate ? 'green' : 'yellow'} px={3} py={1} borderRadius="full" shadow="md" variant="solid">
                {isUpToDate ? 'Live' : 'Live (Edited)'}
              </Badge>
            )}
            {data.deleteDisapprovedReason && (
              <Badge onClick={() => setViewDisapprovalType('delete')} px={3} py={1} borderRadius="full" colorScheme="red" variant="solid" cursor="pointer">
                Delete Disapproved
              </Badge>
            )}
            <CourseNotificationBell />
          </Box>
        </Box>

        <Flex direction="column" p={5} flex={1} justify="space-between">
          <Box mb={4}>
            <InfoPopover>
              <Heading size="md" color={titleColor} mb={1} fontWeight="bold" noOfLines={2} title={data.name}>
                {data.name}
              </Heading>
            </InfoPopover>
            <Text fontSize="xs" color="gray.500" mb={4} fontWeight="medium">
              {data.creator || 'Unknown'} • {formatDateShort(data.created)} • {formattedSize}
            </Text>
            
            <Flex gap={2} wrap="wrap">
              {(isSuperAdmin || isAdmin) && data.org && (
                <Badge
                  colorScheme="gray"
                  variant="subtle"
                  px={2.5} py={0.5} borderRadius="md"
                  fontWeight="medium"
                  fontSize="xs"
                  textTransform="none"
                >
                  Org: {data.org}
                </Badge>
              )}
              <Menu isLazy strategy="fixed">
                <MenuButton
                  as={Button}
                  size="xs"
                  variant="subtle"
                  colorScheme={data.courseType === 'Advanced' ? 'purple' : data.courseType === 'Intermediate' ? 'blue' : 'teal'}
                  cursor={isOnlyReviewer ? "default" : "pointer"}
                  px={2.5} py={0.5} borderRadius="md"
                  fontWeight="semibold"
                  fontSize="xs"
                  rightIcon={isOnlyReviewer ? undefined : <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '9px' }} />}
                >
                  {selectedCourseType}
                </MenuButton>
                <Portal>
                  <MenuList zIndex={999} borderRadius="xl" shadow="xl" border="none" p={2} bg={useColorModeValue("white", "gray.800")}>
                    <MenuItem borderRadius="md" onClick={() => handleCourseTypeChange({ target: { value: 'Beginner' } } as any)}>Beginner</MenuItem>
                    <MenuItem borderRadius="md" onClick={() => handleCourseTypeChange({ target: { value: 'Intermediate' } } as any)}>Intermediate</MenuItem>
                    <MenuItem borderRadius="md" onClick={() => handleCourseTypeChange({ target: { value: 'Advanced' } } as any)}>Advanced</MenuItem>
                  </MenuList>
                </Portal>
              </Menu>
              
              <Menu isLazy strategy="fixed">
                <MenuButton
                  as={Button}
                  size="xs"
                  variant="outline"
                  colorScheme="gray"
                  cursor={isOnlyReviewer ? "default" : "pointer"}
                  px={2.5} py={0.5} borderRadius="md"
                  fontWeight="medium"
                  fontSize="xs"
                  borderColor={useColorModeValue("gray.200", "gray.700")}
                  color={useColorModeValue("gray.600", "gray.300")}
                  rightIcon={isOnlyReviewer ? undefined : <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '9px' }} />}
                >
                  {data.category || 'No Category'}
                </MenuButton>
                <Portal>
                  <MenuList zIndex={999} borderRadius="xl" shadow="xl" border="none" p={2} bg={useColorModeValue("white", "gray.800")}>
                    <MenuItem borderRadius="md" color="gray.500" fontStyle="italic" onClick={() => onEdit({ ...data, category: undefined })}>None (No Category)</MenuItem>
                    {['Programming', 'Design', 'Marketing', 'Finance', 'Data Analysis', 'Customer Support', 'Human Resources', 'Sales', 'Medical'].map(cat => (
                      <MenuItem key={cat} borderRadius="md" fontWeight={data.category === cat ? "bold" : "normal"} onClick={() => onEdit({ ...data, category: cat })}>{cat}</MenuItem>
                    ))}
                  </MenuList>
                </Portal>
              </Menu>

              <Badge
                colorScheme={data.visibility === 'public' ? 'teal' : 'gray'}
                variant="subtle"
                px={2.5} py={0.5} borderRadius="md"
                fontWeight="medium"
                display="inline-flex"
                alignItems="center"
                gap={1.5}
                fontSize="xs"
                textTransform="capitalize"
              >
                <FontAwesomeIcon icon={data.visibility === 'public' ? faGlobe : faLock} style={{ fontSize: '9px' }} />
                {data.visibility === 'public' ? 'Public' : 'Private'}
              </Badge>
            </Flex>
          </Box>

          <Flex pt={4} borderTop="1px dashed" borderColor={useColorModeValue('gray.100', 'gray.700')} justify="flex-end">
            <ActionButtons size="sm" />
          </Flex>
        </Flex>
      </Flex>
    );
  }

  if (viewMode === 'grid') {
    return (
      <Flex
        direction="column"
        bg={glassBg}
        borderRadius="2xl"
        p={5}
        shadow={glassShadow}
        border={glassBorder}
        backdropFilter="blur(20px)"
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        _hover={{ transform: 'translateY(-4px)', shadow: 'xl', borderColor: useColorModeValue('teal.300', 'teal.600') }}
        h="100%"
        justify="space-between"
      >
        <Box>
          <Flex justify="space-between" align="start" mb={4}>
            <InfoPopover>
              <Image
                borderRadius="xl"
                boxSize="64px"
                objectFit="cover"
                src={data.iconUrl ?? 'icon.png'}
                fallbackSrc="https://via.placeholder.com/64"
                shadow="md"
                border="1px solid"
                borderColor={glassBorderColor}
              />
            </InfoPopover>
            <Flex align="center" gap={1.5}>
              <Menu isLazy strategy="fixed">
                <MenuButton
                  as={Button}
                  size="xs"
                  variant="subtle"
                  colorScheme={data.courseType === 'Advanced' ? 'purple' : data.courseType === 'Intermediate' ? 'blue' : 'teal'}
                  borderRadius="md"
                  cursor={isOnlyReviewer ? "default" : "pointer"}
                  px={2.5}
                  h="24px"
                  shadow="none"
                  fontWeight="semibold"
                  fontSize="xs"
                  _hover={isOnlyReviewer ? {} : { opacity: 0.85 }}
                  rightIcon={isOnlyReviewer ? undefined : <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '9px' }} />}
                >
                  {selectedCourseType === 'Intermediate' ? 'INT' : selectedCourseType === 'Advanced' ? 'ADV' : 'BEG'}
                </MenuButton>
                <Portal>
                  <MenuList zIndex={999} borderRadius="xl" shadow="xl" border="none" p={2} bg={useColorModeValue("white", "gray.800")}>
                    <MenuItem borderRadius="md" onClick={() => handleCourseTypeChange({ target: { value: 'Beginner' } } as any)}>Beginner</MenuItem>
                    <MenuItem borderRadius="md" onClick={() => handleCourseTypeChange({ target: { value: 'Intermediate' } } as any)}>Intermediate</MenuItem>
                    <MenuItem borderRadius="md" onClick={() => handleCourseTypeChange({ target: { value: 'Advanced' } } as any)}>Advanced</MenuItem>
                  </MenuList>
                </Portal>
              </Menu>
              <CourseNotificationBell />
            </Flex>
          </Flex>

          <InfoPopover>
            <Heading size="md" color={titleColor} mb={1} fontWeight="bold" noOfLines={1}>{data.name}</Heading>
          </InfoPopover>
          <Flex mb={4} gap={2} wrap="wrap">
            {(isSuperAdmin || isAdmin) && data.org && (
              <Badge
                colorScheme="gray"
                variant="subtle"
                px={2.5}
                py={0.5}
                borderRadius="md"
                fontWeight="medium"
                fontSize="xs"
                textTransform="none"
              >
                Org: {data.org}
              </Badge>
            )}
            <Menu isLazy strategy="fixed">
              <MenuButton
                as={Button}
                size="xs"
                variant={data.category ? "outline" : "ghost"}
                colorScheme="gray"
                borderRadius="md"
                cursor={isOnlyReviewer ? "default" : "pointer"}
                px={2.5}
                h="24px"
                bg={data.category ? (useColorModeValue("white", "gray.900")) : "transparent"}
                color={useColorModeValue("gray.600", "gray.300")}
                borderColor={data.category ? useColorModeValue("gray.200", "gray.700") : "transparent"}
                _hover={isOnlyReviewer ? {} : { bg: useColorModeValue("gray.100", "gray.750") }}
                rightIcon={isOnlyReviewer ? undefined : <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '9px' }} />}
                fontWeight="medium"
                fontSize="xs"
              >
                {data.category || 'None'}
              </MenuButton>
              <Portal>
                <MenuList zIndex={999} borderRadius="xl" shadow="xl" border="none" p={2} bg={useColorModeValue("white", "gray.800")}>
                  <MenuItem borderRadius="md" color="gray.500" fontStyle="italic" onClick={() => onEdit({ ...data, category: undefined })}>None (No Category)</MenuItem>
                  {['Programming', 'Design', 'Marketing', 'Finance', 'Data Analysis', 'Customer Support', 'Human Resources', 'Sales', 'Medical'].map(cat => (
                    <MenuItem key={cat} borderRadius="md" fontWeight={data.category === cat ? "bold" : "normal"} onClick={() => onEdit({ ...data, category: cat })}>{cat}</MenuItem>
                  ))}
                </MenuList>
              </Portal>
            </Menu>

            <Badge
              colorScheme={data.visibility === 'public' ? 'teal' : 'gray'}
              variant="subtle"
              borderRadius="md"
              px={2.5}
              h="24px"
              fontWeight="medium"
              fontSize="xs"
              display="inline-flex"
              alignItems="center"
              gap={1.5}
              textTransform="capitalize"
            >
              <FontAwesomeIcon icon={data.visibility === 'public' ? faGlobe : faLock} style={{ fontSize: '9px' }} />
              {data.visibility === 'public' ? 'Public' : 'Private'}
            </Badge>
          </Flex>

          <Stack spacing={2} mb={4} fontSize="xs">
            <Flex justify="space-between" borderBottom="1px dashed" borderColor={useColorModeValue("gray.100", "gray.750")} pb={1.5}>
              <Text color="gray.400" fontWeight="medium">Size</Text>
              <Text fontWeight="semibold" color={titleColor}>{formattedSize}</Text>
            </Flex>
            <Flex justify="space-between" borderBottom="1px dashed" borderColor={useColorModeValue("gray.100", "gray.750")} pb={1.5}>
              <Text color="gray.400" fontWeight="medium">Created</Text>
              <Text fontWeight="semibold" color={titleColor}>{new Date(data.created).toLocaleDateString()}</Text>
            </Flex>
            <Flex justify="space-between" borderBottom="1px dashed" borderColor={useColorModeValue("gray.100", "gray.750")} pb={1.5}>
              <Text color="gray.400" fontWeight="medium">Edited</Text>
              <Text fontWeight="semibold" color={titleColor}>{new Date(data.edited).toLocaleDateString()}</Text>
            </Flex>
            <Flex justify="space-between" borderBottom="1px dashed" borderColor={useColorModeValue("gray.100", "gray.750")} pb={1.5}>
              <Text color="gray.400" fontWeight="medium">Deployed</Text>
              <Text fontWeight="semibold" color={titleColor}>{data.deployed ? new Date(data.deployed).toLocaleDateString() : 'Never'}</Text>
            </Flex>
            <Flex justify="space-between" align="center" pt={0.5}>
              <Text color="gray.400" fontWeight="medium">Status</Text>
              <Flex alignItems="center" gap={1.5}>
                {data.deployDisapprovedReason ? (
                  <Badge onClick={() => setViewDisapprovalType('deploy')} px={2.5} py={0.5} borderRadius="full" colorScheme="red" variant="solid" cursor="pointer">
                    Deploy Disapproved
                  </Badge>
                ) : data.deleteDisapprovedReason ? (
                  null
                ) : !isDeployed ? (
                  <Badge
                    px={2.5}
                    py={0.5}
                    borderRadius="full"
                    colorScheme="gray"
                    variant="subtle"
                    textTransform="capitalize"
                  >
                    Draft
                  </Badge>
                ) : (
                  <Badge
                    px={2.5}
                    py={0.5}
                    borderRadius="full"
                    colorScheme={isUpToDate ? 'green' : 'yellow'}
                    variant="subtle"
                    textTransform="capitalize"
                  >
                    {isUpToDate ? 'Live' : 'Live (Edited)'}
                  </Badge>
                )}
                {data.deleteDisapprovedReason && (
                  <Badge onClick={() => setViewDisapprovalType('delete')} colorScheme="red" px={2.5} py={0.5} borderRadius="full" variant="solid" cursor="pointer">
                    Delete Disapproved
                  </Badge>
                )}
              </Flex>
            </Flex>
          </Stack>
        </Box>

        <Flex pt={4} borderTop="1px dashed" borderColor="gray.100" justify="flex-end">
          <ActionButtons size="sm" />
        </Flex>
      </Flex>
    );
  }

  // LIST VIEW
  return (
    <Flex
      bg="transparent"
      px={4}
      py={2.5}
      alignItems="center"
      gap={4}
      borderBottom="1px solid"
      borderColor={useColorModeValue("gray.100", "rgba(255, 255, 255, 0.04)")}
      _last={{ borderBottom: "none" }}
      transition="background 0.15s ease"
      w="full"
      _hover={{
        bg: useColorModeValue("gray.50", "rgba(255, 255, 255, 0.02)"),
      }}
      position="relative"
    >
      {/* 1. Course Thumbnail, Title & Badges */}
      <Flex gap={3} align="center" flex={1} minW="260px" overflow="hidden">
        <InfoPopover>
          <Image
            borderRadius="lg"
            boxSize="40px"
            minW="40px"
            objectFit="cover"
            src={data.iconUrl ?? 'icon.png'}
            fallbackSrc="https://via.placeholder.com/40"
            shadow="xs"
            border="1px solid"
            borderColor={useColorModeValue("gray.200", "rgba(255, 255, 255, 0.08)")}
            cursor="pointer"
            onClick={() => navigate(`/${data.id}`)}
          />
        </InfoPopover>
        
        <Flex flexDir="column" gap={1} flex={1} minW={0} overflow="hidden">
          <Flex align="center" gap={2} minW={0}>
            <InfoPopover>
              <Heading 
                size="xs" 
                fontSize="sm"
                color={titleColor} 
                fontWeight="bold" 
                letterSpacing="tight" 
                noOfLines={1} 
                isTruncated
                cursor="pointer"
                _hover={{ color: "#0ea5e9" }}
                onClick={() => navigate(`/${data.id}`)}
              >
                {data.name}
              </Heading>
            </InfoPopover>
            <CourseNotificationBell />
          </Flex>

          <Flex gap={1.5} align="center" flexWrap="nowrap" overflow="hidden">
            {/* Level Tag */}
            <Menu isLazy strategy="fixed">
              <MenuButton
                as={Button}
                size="xs"
                variant="subtle"
                colorScheme={data.courseType === 'Advanced' ? 'purple' : data.courseType === 'Intermediate' ? 'blue' : 'cyan'}
                borderRadius="md"
                cursor={isOnlyReviewer ? "default" : "pointer"}
                px={2}
                h="20px"
                shadow="none"
                flexShrink={0}
                fontWeight="semibold"
                fontSize="10px"
                letterSpacing="0.3px"
                _hover={isOnlyReviewer ? {} : { opacity: 0.85 }}
                rightIcon={isOnlyReviewer ? undefined : <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '8px' }} />}
              >
                {selectedCourseType === 'Intermediate' ? 'INT' : selectedCourseType === 'Advanced' ? 'ADV' : 'BEG'}
              </MenuButton>
              <Portal>
                <MenuList zIndex={999} borderRadius="xl" shadow="2xl" border="1px solid" borderColor={useColorModeValue("gray.200", "rgba(255, 255, 255, 0.08)")} p={1.5} bg={useColorModeValue("white", "#141c28")} outline="none" _focus={{ outline: "none" }}>
                  <MenuItem borderRadius="md" fontSize="xs" bg="transparent" _hover={{ bg: useColorModeValue("gray.100", "rgba(255, 255, 255, 0.06)") }} onClick={() => handleCourseTypeChange({ target: { value: 'Beginner' } } as any)}>Beginner</MenuItem>
                  <MenuItem borderRadius="md" fontSize="xs" bg="transparent" _hover={{ bg: useColorModeValue("gray.100", "rgba(255, 255, 255, 0.06)") }} onClick={() => handleCourseTypeChange({ target: { value: 'Intermediate' } } as any)}>Intermediate</MenuItem>
                  <MenuItem borderRadius="md" fontSize="xs" bg="transparent" _hover={{ bg: useColorModeValue("gray.100", "rgba(255, 255, 255, 0.06)") }} onClick={() => handleCourseTypeChange({ target: { value: 'Advanced' } } as any)}>Advanced</MenuItem>
                </MenuList>
              </Portal>
            </Menu>
            
            {/* Category Tag */}
            <Menu isLazy strategy="fixed">
              <MenuButton
                as={Button}
                size="xs"
                variant="ghost"
                borderRadius="md"
                cursor={isOnlyReviewer ? "default" : "pointer"}
                px={2}
                h="20px"
                bg={data.category ? useColorModeValue("gray.100", "rgba(255, 255, 255, 0.04)") : "transparent"}
                color={useColorModeValue("gray.600", "gray.300")}
                border="1px solid"
                borderColor={useColorModeValue("gray.200", "rgba(255, 255, 255, 0.06)")}
                flexShrink={0}
                fontWeight="medium"
                fontSize="10px"
                _hover={isOnlyReviewer ? {} : { bg: useColorModeValue("gray.200", "rgba(255, 255, 255, 0.08)") }}
                rightIcon={isOnlyReviewer ? undefined : <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '8px' }} />}
              >
                {data.category || 'Category'}
              </MenuButton>
              <Portal>
                <MenuList zIndex={999} borderRadius="xl" shadow="2xl" border="1px solid" borderColor={useColorModeValue("gray.200", "rgba(255, 255, 255, 0.08)")} p={1.5} bg={useColorModeValue("white", "#141c28")} outline="none" _focus={{ outline: "none" }}>
                  <MenuItem borderRadius="md" fontSize="xs" color="gray.500" fontStyle="italic" bg="transparent" _hover={{ bg: useColorModeValue("gray.100", "rgba(255, 255, 255, 0.06)") }} onClick={() => onEdit({ ...data, category: undefined })}>None (No Category)</MenuItem>
                  {['Programming', 'Design', 'Marketing', 'Finance', 'Data Analysis', 'Customer Support', 'Human Resources', 'Sales', 'Medical'].map(cat => (
                    <MenuItem key={cat} borderRadius="md" fontSize="xs" fontWeight={data.category === cat ? "bold" : "normal"} bg="transparent" _hover={{ bg: useColorModeValue("gray.100", "rgba(255, 255, 255, 0.06)") }} onClick={() => onEdit({ ...data, category: cat })}>{cat}</MenuItem>
                  ))}
                </MenuList>
              </Portal>
            </Menu>
            
            {/* Visibility Tag */}
            <Badge
              colorScheme={data.visibility === 'public' ? 'teal' : 'gray'}
              variant="subtle"
              borderRadius="md"
              px={2}
              h="20px"
              flexShrink={0}
              fontWeight="medium"
              fontSize="10px"
              display="inline-flex"
              alignItems="center"
              gap={1}
              textTransform="capitalize"
            >
              <FontAwesomeIcon icon={data.visibility === 'public' ? faGlobe : faLock} style={{ fontSize: '8px' }} />
              {data.visibility === 'public' ? 'Public' : 'Private'}
            </Badge>
          </Flex>
        </Flex>
      </Flex>

      {/* 2. Instructor Column */}
      <Flex display={{ base: 'none', md: 'flex' }} w="140px" flexShrink={0} flexDir="column" align="flex-start" justify="center" gap={0.5}>
        <Text fontSize="xs" color={titleColor} fontWeight="medium" noOfLines={1} isTruncated title={data.creator}>
          {data.creator || 'Unknown'}
        </Text>
        {(isSuperAdmin || isAdmin || isOrgAdmin) && data.org && (
          <Badge
            colorScheme="gray"
            variant="subtle"
            px={1.5}
            py="1px"
            borderRadius="md"
            fontWeight="medium"
            fontSize="10px"
            textTransform="none"
            title={`Org: ${data.org}`}
            noOfLines={1}
            isTruncated
          >
            {data.org}
          </Badge>
        )}
      </Flex>

      {/* 3. Last Updated Column */}
      <Flex align="center" display={{ base: 'none', lg: 'flex' }} w="100px" flexShrink={0}>
        <Tooltip label={`Created: ${formatDateShort(data.created)} • Edited: ${formatDateShort(data.edited)}`} placement="top" hasArrow>
          <Text fontSize="xs" color={useColorModeValue("gray.600", "gray.300")} fontWeight="medium">
            {formatDateShort(data.edited || data.created)}
          </Text>
        </Tooltip>
      </Flex>

      {/* 4. Size / Storage Column */}
      <Flex align="center" display={{ base: 'none', xl: 'flex' }} w="90px" flexShrink={0}>
        <Tooltip
          label={data.stats ? `${data.stats.videos} videos • ${data.stats.images} images • ${data.stats.pdfs} PDFs` : 'Storage size'}
          placement="top"
          hasArrow
        >
          <Text fontSize="xs" color={useColorModeValue("gray.500", "gray.400")} fontWeight="medium" fontFamily="mono">
            {formattedSize}
          </Text>
        </Tooltip>
      </Flex>

      {/* 5. Status Pill Column */}
      <Flex w="130px" flexShrink={0} flexDir="column" gap={1} justify="center" align="center">
        {data.deployDisapprovedReason ? (
          <Badge
            onClick={() => setViewDisapprovalType('deploy')}
            px={2.5}
            py={0.5}
            borderRadius="full"
            bg={useColorModeValue("red.50", "rgba(239, 68, 68, 0.12)")}
            color={useColorModeValue("red.700", "red.300")}
            border="1px solid"
            borderColor={useColorModeValue("red.200", "rgba(239, 68, 68, 0.25)")}
            cursor="pointer"
            fontSize="11px"
            fontWeight="semibold"
          >
            Deploy Disapproved
          </Badge>
        ) : !isDeployed ? (
          <Badge
            display="inline-flex"
            alignItems="center"
            gap={1.5}
            px={2.5}
            py={0.5}
            borderRadius="full"
            bg={useColorModeValue("gray.100", "rgba(255, 255, 255, 0.05)")}
            color={useColorModeValue("gray.600", "gray.400")}
            border="1px solid"
            borderColor={useColorModeValue("gray.200", "rgba(255, 255, 255, 0.08)")}
            fontSize="11px"
            fontWeight="semibold"
            textTransform="capitalize"
          >
            <Box w="6px" h="6px" borderRadius="full" bg="gray.400" />
            Draft
          </Badge>
        ) : (
          <Badge
            display="inline-flex"
            alignItems="center"
            gap={1.5}
            px={2.5}
            py={0.5}
            borderRadius="full"
            bg={isUpToDate ? useColorModeValue("green.50", "rgba(16, 185, 129, 0.12)") : useColorModeValue("orange.50", "rgba(245, 158, 11, 0.12)")}
            color={isUpToDate ? useColorModeValue("green.700", "green.300") : useColorModeValue("orange.700", "orange.300")}
            border="1px solid"
            borderColor={isUpToDate ? useColorModeValue("green.200", "rgba(16, 185, 129, 0.25)") : useColorModeValue("orange.200", "rgba(245, 158, 11, 0.25)")}
            fontSize="11px"
            fontWeight="semibold"
            textTransform="capitalize"
          >
            <Box w="6px" h="6px" borderRadius="full" bg={isUpToDate ? "green.400" : "orange.400"} />
            {isUpToDate ? 'Live' : 'Live (Edited)'}
          </Badge>
        )}
        {data.deleteDisapprovedReason && (
          <Badge
            onClick={() => setViewDisapprovalType('delete')}
            px={2.5}
            py={0.5}
            borderRadius="full"
            bg={useColorModeValue("red.50", "rgba(239, 68, 68, 0.12)")}
            color={useColorModeValue("red.700", "red.300")}
            border="1px solid"
            borderColor={useColorModeValue("red.200", "rgba(239, 68, 68, 0.25)")}
            cursor="pointer"
            fontSize="11px"
            fontWeight="semibold"
          >
            Delete Disapproved
          </Badge>
        )}
      </Flex>

      {/* 6. Actions Column */}
      <Flex w="120px" justify="flex-end" align="center" flexShrink={0}>
        <ActionButtons size="sm" />
      </Flex>

      {/* Disapproval Modal (For Admin to reject) */}
      <Modal isOpen={disapproveType !== null} onClose={() => setDisapproveType(null)}>
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent borderRadius="2xl" overflow="hidden" bg={useColorModeValue("white", "gray.850")}>
          <ModalHeader pb={0}>Disapprove {disapproveType === 'deploy' ? 'Deploy' : 'Delete'} Request</ModalHeader>
          <ModalCloseButton borderRadius="full" />
          <ModalBody py={5}>
            <Stack spacing={4} align="center" textAlign="center" mb={4}>
              <Flex bg={disapproveType === 'deploy' ? "purple.50" : "red.50"} color={disapproveType === 'deploy' ? "purple.500" : "red.500"} p={3.5} borderRadius="full" align="center" justify="center" w="14" h="14">
                <FontAwesomeIcon icon={disapproveType === 'deploy' ? faRocket : faTrash} size="lg" />
              </Flex>
              <Text fontSize="xs" color="gray.500">
                Please provide a reason for rejecting the {disapproveType} request. The instructor will receive this feedback.
              </Text>
            </Stack>
            <FormControl isRequired>
              <Textarea 
                value={disapproveReason}
                onChange={(e) => setDisapproveReason(e.target.value)}
                placeholder="Let the instructor know why this request is being rejected..."
                rows={4}
                borderRadius="xl"
                bg={useColorModeValue("gray.50", "gray.900/60")}
                borderColor={useColorModeValue("gray.200", "gray.750")}
                _focus={{ borderColor: "green.500", boxShadow: "none" }}
                fontSize="xs"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter p={0}>
            <Flex w="100%">
              <Button 
                variant="solid" 
                bg="gray.400" 
                color="white" 
                borderRadius="0"
                h="48px"
                flex={1}
                _hover={{ bg: "gray.500" }}
                onClick={() => setDisapproveType(null)}
              >
                Dismiss
              </Button>
              <Button 
                variant="solid"
                colorScheme={disapproveType === 'deploy' ? "green" : "red"} 
                isDisabled={disapproveReason.trim() === ''}
                onClick={handleDisapprove}
                borderRadius="0"
                h="48px"
                flex={1}
              >
                Submit
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Reason/Cancel Modal (For Instructor) */}
      <Modal isOpen={viewDisapprovalType !== null} onClose={() => setViewDisapprovalType(null)}>
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent borderRadius="2xl" overflow="hidden" bg={useColorModeValue("white", "gray.850")}>
          <ModalHeader pb={0}>
            {viewDisapprovalType === 'cancelDeploy' || viewDisapprovalType === 'cancelDelete' 
              ? 'Cancel Request' 
              : 'Request Disapproved'}
          </ModalHeader>
          <ModalCloseButton borderRadius="full" />
          <ModalBody py={5}>
            {viewDisapprovalType === 'cancelDeploy' || viewDisapprovalType === 'cancelDelete' ? (
              <Stack spacing={4} align="center" textAlign="center">
                <Flex bg="orange.50" color="orange.500" p={3.5} borderRadius="full" align="center" justify="center" w="14" h="14">
                  <FontAwesomeIcon icon={faTimes} size="lg" />
                </Flex>
                <Text color="gray.600" fontSize="sm">
                  Are you sure you want to cancel your pending <b>{viewDisapprovalType === 'cancelDeploy' ? 'deploy' : 'delete'}</b> request?
                </Text>
              </Stack>
            ) : (
              <Stack spacing={4}>
                <Flex align="center" gap={3} bg="red.50/50" p={3.5} borderRadius="xl" border="1px solid" borderColor="red.100/60">
                  <Flex bg="red.50" color="red.500" p={2} borderRadius="full" align="center" justify="center" w="10" h="10">
                    <FontAwesomeIcon icon={faTimes} />
                  </Flex>
                  <Box>
                    <Text fontWeight="bold" color="red.800" fontSize="xs">Reason from Admin:</Text>
                    <Text color="red.700" fontSize="xs">
                      {viewDisapprovalType === 'deploy' ? data.deployDisapprovedReason : data.deleteDisapprovedReason}
                    </Text>
                  </Box>
                </Flex>
              </Stack>
            )}
          </ModalBody>
          <ModalFooter p={0}>
            <Flex w="100%">
              <Button 
                variant="solid" 
                bg="gray.400" 
                color="white" 
                borderRadius="0"
                h="48px"
                flex={1}
                _hover={{ bg: "gray.500" }}
                onClick={() => setViewDisapprovalType(null)}
              >
                Dismiss
              </Button>
              {isCreatorOrCollab && (
                <Button 
                  variant="solid"
                  colorScheme="red" 
                  borderRadius="0"
                  h="48px"
                  flex={1}
                  onClick={() => {
                    if (viewDisapprovalType === 'cancelDeploy') {
                      onEdit({ ...data, deployRequested: false });
                      logCourseAction(data.id, "Deploy Request Cancelled", `User ${currentUserEmail} cancelled deploy request.`);
                    } else if (viewDisapprovalType === 'cancelDelete') {
                      onEdit({ ...data, deleteRequested: false });
                      logCourseAction(data.id, "Delete Request Cancelled", `User ${currentUserEmail} cancelled delete request.`);
                    } else {
                      handleAcknowledgeDisapproval(viewDisapprovalType as any);
                    }
                    setViewDisapprovalType(null);
                  }}
                >
                  {viewDisapprovalType === 'cancelDeploy' || viewDisapprovalType === 'cancelDelete' ? 'Confirm Cancel' : 'Remove & Re-request'}
                </Button>
              )}
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Audit Logs Modal */}
      <Modal isOpen={viewingLogs} onClose={() => setViewingLogs(false)} size="4xl" scrollBehavior="inside">
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader>Audit Logs - {data.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {logsLoading ? (
              <Flex py={10} justify="center"><Spinner size="xl" color="green.500" thickness="4px" /></Flex>
            ) : logs.length === 0 ? (
              <Text py={10} color="gray.500" textAlign="center">No logs found for this course.</Text>
            ) : (
              <Box overflowX="auto" borderRadius="lg" border="1px solid" borderColor={glassBorderColor}>
                <Table variant="simple" size="sm">
                  <Thead bg={useColorModeValue('gray.50', 'gray.900/60')}>
                    <Tr>
                      <Th py={3} px={4} color="gray.400" fontSize="10px" fontWeight="extrabold" letterSpacing="wider">Timestamp</Th>
                      <Th py={3} px={4} color="gray.400" fontSize="10px" fontWeight="extrabold" letterSpacing="wider">User</Th>
                      <Th py={3} px={4} color="gray.400" fontSize="10px" fontWeight="extrabold" letterSpacing="wider">Action</Th>
                      <Th py={3} px={4} color="gray.400" fontSize="10px" fontWeight="extrabold" letterSpacing="wider">Details</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {logs.map((log) => {
                      const badgeStyle = getActionBadge(log.action);
                      return (
                        <Tr key={log.id} _hover={{ bg: useColorModeValue("gray.50", "rgba(255, 255, 255, 0.04)") }}>
                          <Td py={3} px={4} whiteSpace="nowrap" color="gray.500" fontSize="xs" verticalAlign="top">
                            {new Date(log.timestamp).toLocaleString()}
                          </Td>
                          <Td py={3} px={4} fontWeight="semibold" color={titleColor} fontSize="xs" verticalAlign="top" maxW="200px" isTruncated title={log.userEmail}>
                            {log.userEmail}
                          </Td>
                          <Td py={3} px={4} verticalAlign="top">
                            <Badge bg={badgeStyle.bg} color={badgeStyle.color} px={2} py={0.5} borderRadius="md" fontSize="10px" fontWeight="700" textTransform="uppercase">
                              {log.action}
                            </Badge>
                          </Td>
                          <Td py={3} px={4} verticalAlign="top">
                            <DetailsCell details={log.details} />
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setViewingLogs(false)}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Confirm Archive Deployed Course Modal */}
      <Modal isOpen={confirmArchive} onClose={() => setConfirmArchive(false)}>
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent borderRadius="2xl" overflow="hidden" bg={useColorModeValue("white", "gray.850")}>
          <ModalHeader pb={0}>Archive Deployed Course</ModalHeader>
          <ModalCloseButton borderRadius="full" />
          <ModalBody py={5}>
            <Stack spacing={4} align="center" textAlign="center">
              <Flex bg="yellow.50" color="yellow.500" p={3.5} borderRadius="full" align="center" justify="center" w="14" h="14">
                <FontAwesomeIcon icon={faBoxArchive} size="lg" color="#eab308" />
              </Flex>
              <Text fontSize="sm" fontWeight="bold" color="gray.700">
                "{data.name}" is currently LIVE / DEPLOYED.
              </Text>
              <Text fontSize="xs" color="gray.500">
                Moving this course to Archive will automatically un-deploy it from live student access. You can restore it anytime from the Archive tab.
              </Text>
            </Stack>
          </ModalBody>
          <ModalFooter p={0}>
            <Flex w="100%">
              <Button 
                variant="solid" 
                bg="gray.400" 
                color="white" 
                borderRadius="0"
                h="48px"
                flex={1}
                _hover={{ bg: "gray.500" }}
                onClick={() => setConfirmArchive(false)}
              >
                Cancel
              </Button>
              <Button 
                variant="solid"
                colorScheme="yellow" 
                color="black"
                borderRadius="0"
                h="48px"
                flex={1}
                onClick={() => {
                  setConfirmArchive(false);
                  if (onArchive) onArchive(data);
                  else onEdit({ ...data, archived: true });
                }}
              >
                Confirm & Archive
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default AppCard;
